export const convertImageToJpg = (file) => {
  return new Promise((resolve, reject) => {
    // If it's not an image, or if it's already a JPG, we might still want to re-encode to ensure standard format if that's the goal.
    // But usually "unsupported image format" implies things like HEIC or WebP or PNG if the backend only supports JPG.
    // The user said "normalize to jpg". So we should convert PNG, WebP, etc.
    // If it's already JPEG, we could skip, but re-encoding ensures it's a "clean" JPEG.
    // However, re-encoding JPEG degrades quality.
    // Let's assume we convert all images to JPEG.
    
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      // Fill white background for transparent images (like PNG)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas to Blob conversion failed'));
          return;
        }
        const newName = file.name.replace(/\.[^/.]+$/, "") + ".jpg";
        const newFile = new File([blob], newName, { type: 'image/jpeg' });
        resolve(newFile);
      }, 'image/jpeg', 0.9);
    };
    
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    
    img.src = url;
  });
};

export const FOOD_INFERENCE_MAX_IMAGE_BYTES = 1024 * 1024;
export const FOOD_INFERENCE_MAX_TOTAL_BYTES = 2 * 1024 * 1024;
export const FOOD_INFERENCE_TARGET_PIXELS = 1000000;

export const getFoodInferenceImageByteBudget = (imageCount) => Math.min(
  FOOD_INFERENCE_MAX_IMAGE_BYTES,
  Math.floor(FOOD_INFERENCE_MAX_TOTAL_BYTES / Math.max(1, imageCount))
);

const FOOD_INFERENCE_ENCODINGS = [
  { pixels: FOOD_INFERENCE_TARGET_PIXELS, quality: 0.82 },
  { pixels: 750000, quality: 0.72 },
  { pixels: 500000, quality: 0.62 },
  { pixels: 300000, quality: 0.55 },
  { pixels: 180000, quality: 0.5 },
];

const createImagePreparationCanceledError = () => Object.assign(new Error('canceled'), {
  name: 'AbortError',
  code: 'ERR_CANCELED',
});

export const prepareFoodInferenceImage = (
  file,
  maxBytes = FOOD_INFERENCE_MAX_IMAGE_BYTES,
  signal = null
) => {
  if (signal?.aborted) return Promise.reject(createImagePreparationCanceledError());
  if (!file?.type?.startsWith('image/') || file.size <= maxBytes) {
    return Promise.resolve(file);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    let settled = false;
    const cleanup = () => {
      signal?.removeEventListener('abort', abortPreparation);
      URL.revokeObjectURL(url);
    };
    const finish = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const abortPreparation = () => {
      img.src = '';
      fail(createImagePreparationCanceledError());
    };
    signal?.addEventListener('abort', abortPreparation, { once: true });

    img.onload = async () => {
      try {
        for (const [index, encoding] of FOOD_INFERENCE_ENCODINGS.entries()) {
          if (signal?.aborted) throw createImagePreparationCanceledError();
          const scale = Math.min(1, Math.sqrt(encoding.pixels / (img.width * img.height)));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.floor(img.width * scale));
          canvas.height = Math.max(1, Math.floor(img.height * scale));
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas context unavailable');
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const blob = await new Promise((blobResolve, blobReject) => {
            canvas.toBlob(
              value => value ? blobResolve(value) : blobReject(new Error('Canvas to Blob conversion failed')),
              'image/jpeg',
              encoding.quality
            );
          });
          if (settled) return;
          if (signal?.aborted) throw createImagePreparationCanceledError();
          const isLastAttempt = index === FOOD_INFERENCE_ENCODINGS.length - 1;
          if (blob.size <= maxBytes || isLastAttempt) {
            if (blob.size > maxBytes) {
              const error = new Error('This photo could not be prepared for upload. Please choose a smaller photo.');
              error.code = 'image_prepare_failed';
              throw error;
            }
            const name = `${file.name.replace(/\.[^/.]+$/, '')}.jpg`;
            finish(new File([blob], name, {
              type: 'image/jpeg',
              lastModified: file.lastModified,
            }));
            return;
          }
        }
      } catch (error) {
        fail(error);
      }
    };

    img.onerror = () => {
      const error = new Error('This photo could not be prepared for upload. Please choose a different photo.');
      error.code = 'image_prepare_failed';
      fail(error);
    };
    img.src = url;
  });
};
